/**
 * Some subscription apps (e.g. Seal Subscriptions) auto-inject their own
 * purchase UI into the page via an app embed script. That can duplicate this
 * theme's own purchase-type selector (see purchase-type-selector.js) and has
 * been known to hide/replace the accelerated checkout buttons in the
 * buy-buttons block - which the client wants kept intact. This removes any
 * such injected widget the moment it appears inside the product form or the
 * cart drawer's upsell card, so this theme's own UI stays the only thing
 * shown there.
 *
 * IMPORTANT: the selector list below is a best-effort guess at Seal
 * Subscriptions' common class/id naming, made without access to the live
 * injected markup. If it doesn't fully catch the widget, open the browser
 * devtools on the storefront, inspect the injected element, and add its
 * actual id/class to THIRD_PARTY_WIDGET_SELECTORS below. The cleanest fix,
 * if available, is still to turn the app off under
 * Theme Editor > App embeds.
 */
const THIRD_PARTY_WIDGET_SELECTORS = [
  '[id*="seal-subs" i]',
  '[class*="seal-subs" i]',
  '[id*="sealsubscription" i]',
  '[class*="sealsubscription" i]',
  '[id*="seal-subscription" i]',
  '[class*="seal-subscription" i]',
  '[data-seal-subscription]',
  '[data-seal-app]',
  'seal-subscription-widget',
].join(', ');

// Scoped to the product form and the cart drawer's upsell card - never the
// whole page - so nothing outside those is ever touched.
const WATCH_SCOPES = '.product-template, .product-upsell';

function purge(root) {
  if (root.matches?.(THIRD_PARTY_WIDGET_SELECTORS)) {
    root.remove();
    return;
  }
  root.querySelectorAll?.(THIRD_PARTY_WIDGET_SELECTORS).forEach((el) => el.remove());
}

document.querySelectorAll(WATCH_SCOPES).forEach(purge);

// Cart drawer content can be swapped wholesale via Shopify's Section
// Rendering API after an add-to-cart, which would orphan a scope-specific
// observer - watching the whole document keeps catching both re-created
// scope containers and widgets injected into them later, while still only
// ever purging nodes that are themselves (or live inside) a watch scope.
new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType !== 1) return;

      // A (re)created scope container - purge anything already inside it.
      if (node.matches(WATCH_SCOPES)) purge(node);
      node.querySelectorAll?.(WATCH_SCOPES).forEach(purge);

      // The widget itself, added directly inside an existing scope.
      if (node.closest(WATCH_SCOPES)) purge(node);
    });
  });
}).observe(document.body, { childList: true, subtree: true });
